/**
 * Folk & Shepherd — configurator v2 controller.
 *
 * Wires the frame-engine.js visual engine to the Size/Frame/Mount controls,
 * computes prices and option labels, resolves the real Shopify variant
 * once connected, and hands off to the theme's own <product-form> for
 * add to cart (see the build plan's "UI spec" and "Variant model and
 * pricing" sections — this file implements both).
 *
 * Two price sources, switched by the section's "price_source" setting:
 *  - "placeholder": prices come from config.pricing.placeholder (GBP only).
 *    No real variants exist yet, so Add to cart stays disabled and there's
 *    no ?variant= URL sync.
 *  - "variants": prices and availability come from the product's real
 *    variants (data-configurator-v2-variants JSON, option1/2/3 matched
 *    directly — never by splitting a lookup-key string). variant.price is
 *    whatever Liquid rendered it as, which Shopify already converts to the
 *    visitor's active market currency at render time — the same approach
 *    already proven correct on the live configurator (cart.currency.iso_code
 *    + variant.price, no client-side conversion math).
 */
(function () {
  'use strict';

  function qs(root, sel) { return root.querySelector(sel); }
  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  class ConfiguratorV2 extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.priceSource = this.dataset.priceSource === 'variants' ? 'variants' : 'placeholder';
      this.orientation = this.dataset.orientation === 'portrait' ? 'portrait' : this.dataset.orientation === 'square' ? 'square' : 'landscape';
      // sections/main-product.liquid sets data-currency (cart.currency.iso_code).
      this.currencyIso = this.dataset.currency || this.dataset.currencyIso || 'GBP';
      this.locale = this.dataset.locale || undefined;

      this.stageEl = qs(this, '[data-configurator-v2-stage]');
      this.frameAssets = parseJSON(qs(this, '[data-configurator-v2-frame-assets]'), {});
      this.artworkUrl = (parseJSON(qs(this, '[data-configurator-v2-artwork]'), {}) || {}).url || '';
      this.variants = parseJSON(qs(this, '[data-configurator-v2-variants]'), []) || [];

      this.sizeInputs = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-v2-sizes] input[type="radio"]'));
      this.frameSelect = qs(this, '[data-configurator-v2-frame]');
      this.mountSelect = qs(this, '[data-configurator-v2-mount]');
      this.mountHelper = qs(this, '[data-configurator-v2-mount-helper]');
      this.finishedSizeEl = qs(this, '[data-configurator-v2-finished-size]');
      this.priceEl = qs(this, '[data-configurator-v2-price]');
      this.hiddenInput = qs(this, '[data-configurator-v2-variant-input]');
      this.submitButton = qs(this, '[data-configurator-v2-submit]');
      this.form = this.querySelector('form');
      this.imageView = qs(this, '[data-configurator-v2-image-view]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-v2-show-mockup], [data-configurator-v2-show-image]'));
      this.mockupThumb = qs(this, '[data-configurator-v2-show-mockup]');

      if (!this.stageEl || !this.frameSelect || !this.mountSelect || !window.FolkFrameEngine) {
        return; // malformed markup or engine failed to load — fail quiet, no-JS fallback still works
      }

      this.dropdowns = {
        frame: this.enhanceDropdown(qs(this, '[data-configurator-v2-dropdown="frame"]'), this.frameSelect),
        mount: this.enhanceDropdown(qs(this, '[data-configurator-v2-dropdown="mount"]'), this.mountSelect)
      };

      this.assetUrl = function (assetsKey, piece) {
        return this.frameAssets[assetsKey + '-' + piece] || '';
      }.bind(this);

      var checkedSize = this.sizeInputs.filter(function (i) { return i.checked; })[0];
      this.state = {
        size: checkedSize ? checkedSize.value : (this.sizeInputs[0] && this.sizeInputs[0].value),
        frame: this.frameSelect.value,
        mount: this.mountSelect.value
      };

      fetch(this.dataset.configUrl)
        .then(function (r) { return r.json(); })
        .then(function (config) {
          this.config = config;
          // "Unframed" / "No mount": whichever option has no frame/mount
          // entry in the engine config, so this follows the product's own
          // option names rather than hardcoding them.
          this.noFrame = this.firstUnmatched(this.frameSelect, config.frames);
          this.noMount = this.firstUnmatched(this.mountSelect, config.mounts);
          this.applyUrlVariant();
          this.bindEvents();
          this.render();
          this.preloadFrames();
          this.observeResize();
        }.bind(this))
        .catch(function (err) {
          console.error('configurator-v2: failed to load config', err);
        });
    }

    applyUrlVariant() {
      if (this.priceSource !== 'variants') return;
      var params = new URLSearchParams(window.location.search);
      var variantId = params.get('variant');
      if (!variantId) return;
      var variant = this.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
      if (!variant) return;

      this.state = { size: variant.option1, frame: variant.option2, mount: variant.option3 };
      var sizeInput = this.sizeInputs.filter(function (i) { return i.value === variant.option1; })[0];
      if (sizeInput) sizeInput.checked = true;
      if (this.frameOptionExists(variant.option2)) this.frameSelect.value = variant.option2;
      if (this.mountOptionExists(variant.option3)) this.mountSelect.value = variant.option3;
    }

    // ---- option value -> engine config key ------------------------------
    //
    // The product's option values ("Box frame – Oak", "White mount",
    // "16 × 24 in") are written for shoppers; assets/frame-engine-config.json
    // keys ("Box Frame - Oak", "White Mount", '24×16"') are the engine's.
    // Match them loosely (case, dash style, spacing) rather than keeping two
    // copies of every name in sync.

    normKey(value) {
      return String(value || '').toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ').trim();
    }

    configKey(map, value) {
      var target = this.normKey(value);
      return Object.keys(map || {}).filter(function (k) { return this.normKey(k) === target; }, this)[0] || null;
    }

    firstUnmatched(select, map) {
      var opt = Array.prototype.filter.call(select.options, function (o) { return !this.configKey(map, o.value); }, this)[0];
      return opt ? opt.value : select.options[0] && select.options[0].value;
    }

    // Sizes match on their two numbers, whichever order they're written in
    // ("12 × 18 in" portrait is the config's 18×12" entry).
    sizeKey(value) {
      var nums = (String(value).match(/\d+(?:\.\d+)?/g) || []).slice(0, 2).map(Number);
      if (nums.length < 2) return null;
      var long = Math.max(nums[0], nums[1]), short = Math.min(nums[0], nums[1]);
      var sizes = this.config.sizes;
      return Object.keys(sizes).filter(function (k) {
        return Math.max(sizes[k].w_in, sizes[k].h_in) === long && Math.min(sizes[k].w_in, sizes[k].h_in) === short;
      })[0] || null;
    }

    frameOptionExists(value) {
      return Array.prototype.some.call(this.frameSelect.options, function (o) { return o.value === value; });
    }

    mountOptionExists(value) {
      return Array.prototype.some.call(this.mountSelect.options, function (o) { return o.value === value; });
    }

    bindEvents() {
      this.sizeInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          this.state.size = input.value;
          this.render();
        }.bind(this));
      }, this);

      this.frameSelect.addEventListener('change', function () {
        this.state.frame = this.frameSelect.value;
        this.render();
      }.bind(this));

      this.mountSelect.addEventListener('change', function () {
        this.state.mount = this.mountSelect.value;
        this.render();
      }.bind(this));

      this.thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.hasAttribute('data-configurator-v2-show-mockup')) {
            this.showMockup();
          } else {
            this.showImage(btn.getAttribute('data-configurator-v2-show-image'));
          }
          this.setActiveThumb(btn);
        }.bind(this));
      }, this);
    }

    // ---- stage / other-images view -----------------------------------

    showMockup() {
      if (this.stageEl) this.stageEl.hidden = false;
      if (this.imageView) this.imageView.hidden = true;
      // Re-render every time, not just on a real control change: the
      // stage may have been hidden (and so sized 0) while another image
      // was showing, which is exactly what goes stale otherwise — see
      // renderStageOnly()'s comment. Cheap, and guarantees correct
      // alignment on every click back to the mockup.
      return this.config ? this.renderStage() : null;
    }

    showImage(url) {
      if (!this.imageView || !url) return;
      this.imageView.src = url;
      this.imageView.hidden = false;
      if (this.stageEl) this.stageEl.hidden = true;
    }

    setActiveThumb(activeBtn) {
      this.thumbs.forEach(function (btn) {
        var active = btn === activeBtn;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
    }

    observeResize() {
      if (typeof ResizeObserver === 'undefined') return;
      var raf = null;
      var ro = new ResizeObserver(function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () { this.renderStageOnly(); }.bind(this));
      }.bind(this));
      ro.observe(this.stageEl);
    }

    // ---- pricing ----------------------------------------------------

    frameFamily(frameValue) {
      var f = this.config.frames[this.configKey(this.config.frames, frameValue)];
      return f ? f.family : null;
    }

    // Full price of a Size × Frame × Mount combination, in minor units, or
    // null when that variant doesn't exist.
    price(sizeValue, frameValue, mountValue) {
      if (this.priceSource === 'placeholder') {
        var p = this.config.pricing.placeholder;
        var base = p.no_frame[this.sizeKey(sizeValue)] || 0;
        var family = this.frameFamily(frameValue);
        var frameSurcharge = family ? (p.family_surcharge[family] || 0) : 0;
        var mounted = !!family && !!this.configKey(this.config.mounts, mountValue);
        var mountSurcharge = mounted ? (p.mount_surcharge || 0) : 0;
        return base + frameSurcharge + mountSurcharge;
      }
      var variant = this.findVariant(sizeValue, frameValue, mountValue);
      return variant ? variant.price : null;
    }

    // Add-ons aren't stored anywhere; they're the difference between two
    // real variant prices, so they're already in the shopper's currency
    // with Markets rounding applied. null if either variant is missing.
    //   frame add-on = price(size, frame, No mount) − price(size, Unframed, No mount)
    //   mount add-on = price(size, frame, mount)    − price(size, frame, No mount)
    addon(withPrice, withoutPrice) {
      if (withPrice == null || withoutPrice == null) return null;
      return withPrice - withoutPrice;
    }

    findVariant(sizeValue, frameValue, mountValue) {
      return this.variants.filter(function (v) {
        return v.option1 === sizeValue && v.option2 === frameValue && v.option3 === mountValue;
      })[0] || null;
    }

    formatMoney(minorUnits, opts) {
      opts = opts || {};
      if (minorUnits == null) return '—';
      var currency = this.priceSource === 'placeholder' ? (this.config.pricing.placeholder.currency || 'GBP') : this.currencyIso;
      var locale = this.priceSource === 'placeholder' ? 'en-GB' : this.locale;
      var amount = minorUnits / 100;
      var formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: (amount % 1 === 0) ? 0 : 2,
        maximumFractionDigits: 2
      });
      var formatted = formatter.format(Math.abs(amount));
      if (opts.signed && minorUnits !== 0) {
        return (minorUnits > 0 ? '+ ' : '− ') + formatted;
      }
      return formatted;
    }

    // ---- rendering ----------------------------------------------------

    stageOpts() {
      var stagePx = Math.round(this.stageEl.clientWidth) || 800;
      return {
        config: this.config,
        frameValue: this.configKey(this.config.frames, this.state.frame) || undefined,
        mountValue: this.configKey(this.config.mounts, this.state.mount) || undefined,
        sizeValue: this.sizeKey(this.state.size),
        orientation: this.orientation,
        artworkUrl: this.artworkUrl,
        assetUrl: this.assetUrl,
        stagePx: stagePx,
        printSpecs: true
      };
    }

    // Draws the mockup once this choice's frame images are loaded (the
    // previous mockup stays up until then — no half-drawn frame), but
    // returns the geometry straight away so the size line updates at once.
    renderStage() {
      var opts = this.stageOpts();
      window.FolkFrameEngine.renderWhenReady(this.stageEl, opts);
      return window.FolkFrameEngine.computeGeometry(opts.config, opts.frameValue, opts.mountValue,
        opts.sizeValue, opts.orientation, opts.stagePx, opts.printSpecs);
    }

    // Every frame colour's side images, fetched quietly once the page has
    // settled, so switching frames is instant.
    preloadFrames() {
      var urls = Object.keys(this.frameAssets).map(function (k) { return this.frameAssets[k]; }, this);
      var go = function () { window.FolkFrameEngine.preload(urls.concat(this.artworkUrl)); }.bind(this);
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 3000 });
      else setTimeout(go, 1500);
    }

    // Resize only ever changes the stage's pixel size, never the
    // state-dependent text/labels, so skip recomputing those.
    //
    // Hiding the stage (switching to view another image) collapses its
    // clientWidth to 0, which the ResizeObserver below reports as a
    // resize — rendering then with no guard falls back to a hardcoded
    // 800px stage (clientWidth || 800 means 0 is falsy), baking in a
    // mockup sized for the wrong box. Showing the stage again just
    // un-hides that stale, wrongly-sized render. Skip entirely while
    // there's no real size to render into; showMockup() re-renders
    // properly once it's actually visible again.
    renderStageOnly() {
      if (!this.config) return;
      if (!this.stageEl || this.stageEl.hidden || !this.stageEl.clientWidth) return;
      this.renderStage();
    }

    render() {
      if (!this.config) return;
      // Any Size/Frame/Mount change only means something in the live
      // mockup, so any call to render() (every change fires one) brings
      // the shopper back to it if they'd switched to viewing another
      // image. showMockup() already re-renders the stage as part of
      // showing it, so this doesn't render it a second time.
      this.resolveMount();
      var geo = this.showMockup();
      if (this.mockupThumb) this.setActiveThumb(this.mockupThumb);
      this.updateFinishedSize(geo);
      this.updateFrameLabels();
      this.updateMountLabels();
      this.updateMountDisabledState();
      this.updatePrice();
      this.updateVariant();
    }

    updateFinishedSize(geo) {
      if (!this.finishedSizeEl) return;
      // Unframed: the size picked, plus the print's real cm size.
      // Framed: the frame's outside size (config.sizes[...] per-size
      // specs; a mounted box frame is a bigger frame).
      function num(v) { return String(Number(v.toFixed(1))); } // 12.0 -> "12", 62.4 -> "62.4"
      function dims(w, h, unit) { return num(w) + ' × ' + num(h) + ' ' + unit; }
      var mm = geo.mm;
      this.finishedSizeEl.textContent = geo.framed
        ? 'Framed size approx ' + dims(mm.outerW / 25.4, mm.outerH / 25.4, 'in') + ' (' + dims(mm.outerW / 10, mm.outerH / 10, 'cm') + ')'
        : 'Print size ' + dims(mm.sizeInW, mm.sizeInH, 'in') + ' (' + dims(mm.printW / 10, mm.printH / 10, 'cm') + ')';
    }

    updateFrameLabels() {
      var baseline = this.price(this.state.size, this.noFrame, this.noMount);
      var addons = {};
      Array.prototype.forEach.call(this.frameSelect.options, function (opt) {
        if (opt.value !== this.noFrame) {
          addons[opt.value] = this.addon(this.price(this.state.size, opt.value, this.noMount), baseline);
        }
        opt.textContent = this.optionText(opt.value, addons[opt.value]);
      }, this);
      this.syncDropdown(this.dropdowns.frame, this.state.frame, addons);
    }

    updateMountLabels() {
      // Priced against the frame actually selected. Mounts that frame can't
      // take (Unframed, thin frames) show no add-on and are disabled.
      var baseline = this.price(this.state.size, this.state.frame, this.noMount);
      var addons = {};
      Array.prototype.forEach.call(this.mountSelect.options, function (opt) {
        if (opt.value !== this.noMount && this.mountAvailable(this.state.frame, opt.value)) {
          addons[opt.value] = this.addon(this.price(this.state.size, this.state.frame, opt.value), baseline);
        }
        opt.textContent = this.optionText(opt.value, addons[opt.value]);
      }, this);
      this.syncDropdown(this.dropdowns.mount, this.state.mount, addons);
    }

    // A mount can go with a frame only if the frame's family takes one
    // (SIM thin frames don't: families.thin.mountable = false) and, with
    // real variants, that exact Size × Frame × Mount variant exists.
    mountAvailable(frameValue, mountValue) {
      if (mountValue === this.noMount) return true;
      if (frameValue === this.noFrame) return false;
      var family = this.config.families[this.frameFamily(frameValue)];
      if (family && family.mountable === false) return false;
      if (this.priceSource === 'variants') return !!this.findVariant(this.state.size, frameValue, mountValue);
      return true;
    }

    hasAddon(amount) {
      return amount != null && isFinite(amount) && amount !== 0;
    }

    addonText(amount) {
      return this.hasAddon(amount) ? this.formatMoney(amount, { signed: true }) : '';
    }

    optionText(value, amount) {
      return this.hasAddon(amount) ? value + '  ' + this.addonText(amount) : value;
    }

    updateMountDisabledState() {
      var available = {};
      Array.prototype.forEach.call(this.mountSelect.options, function (opt) {
        available[opt.value] = this.mountAvailable(this.state.frame, opt.value);
        opt.disabled = !available[opt.value];
      }, this);
      // Greyed out entirely when the frame takes no mount at all.
      var disabled = Object.keys(available).every(function (v) { return v === this.noMount || !available[v]; }, this);
      this.mountSelect.disabled = disabled;
      this.mountSelect.setAttribute('aria-disabled', String(disabled));
      if (this.dropdowns.mount) {
        this.dropdowns.mount.trigger.disabled = disabled;
        this.dropdowns.mount.options.forEach(function (opt) {
          opt.setAttribute('aria-disabled', String(!available[opt.dataset.value]));
        });
        if (disabled) this.closeDropdown(this.dropdowns.mount);
      }
      if (this.mountHelper) {
        this.mountHelper.hidden = !disabled;
        this.mountHelper.textContent = this.state.frame === this.noFrame
          ? 'Add a frame to choose a mount.'
          : 'This frame doesn’t take a mount.';
      }
    }

    // Falls back to "No mount" whenever the current mount isn't available
    // for the new size/frame (Unframed, a thin frame, a missing variant).
    resolveMount() {
      if (this.mountAvailable(this.state.frame, this.state.mount)) return;
      this.state.mount = this.noMount;
      this.mountSelect.value = this.noMount;
    }

    // ---- swatch dropdowns ----------------------------------------------
    //
    // The design's Frame/Mount pickers show a swatch per option, which a
    // native <select> can't. The select stays the source of truth (and the
    // no-JS fallback): picking an option here sets select.value and fires
    // the same 'change' event the rest of this file already listens to.

    enhanceDropdown(root, select) {
      if (!root || !select) return null;
      var dd = {
        root: root,
        select: select,
        trigger: qs(root, '.cv2-dropdown__trigger'),
        current: qs(root, '[data-cv2-current]'),
        list: qs(root, '[role="listbox"]'),
        options: Array.prototype.slice.call(root.querySelectorAll('[role="option"]'))
      };
      var native = select.closest('[data-configurator-v2-native]');
      if (native) native.hidden = true;
      root.hidden = false;
      this.syncDropdown(dd, select.value, {});

      dd.options.forEach(function (opt, i) {
        opt.id = select.id + '-opt-' + i;
        opt.addEventListener('click', function () { this.chooseOption(dd, opt); }.bind(this));
      }, this);

      dd.trigger.addEventListener('click', function () {
        if (dd.list.hidden) this.openDropdown(dd); else this.closeDropdown(dd);
      }.bind(this));

      dd.trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.openDropdown(dd);
        }
      }.bind(this));

      dd.list.addEventListener('keydown', function (e) {
        var idx = dd.options.indexOf(dd.active);
        if (e.key === 'ArrowDown') { e.preventDefault(); this.setActiveOption(dd, dd.options[Math.min(idx + 1, dd.options.length - 1)]); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActiveOption(dd, dd.options[Math.max(idx - 1, 0)]); }
        else if (e.key === 'Home') { e.preventDefault(); this.setActiveOption(dd, dd.options[0]); }
        else if (e.key === 'End') { e.preventDefault(); this.setActiveOption(dd, dd.options[dd.options.length - 1]); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (dd.active) this.chooseOption(dd, dd.active); }
        else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(dd, true); }
        else if (e.key === 'Tab') { this.closeDropdown(dd); }
      }.bind(this));

      document.addEventListener('click', function (e) {
        if (!dd.list.hidden && !root.contains(e.target)) this.closeDropdown(dd);
      }.bind(this));

      return dd;
    }

    openDropdown(dd) {
      if (dd.trigger.disabled) return;
      ['frame', 'mount'].forEach(function (k) {
        if (this.dropdowns[k] && this.dropdowns[k] !== dd) this.closeDropdown(this.dropdowns[k]);
      }, this);
      dd.list.hidden = false;
      dd.root.classList.add('is-open');
      dd.trigger.setAttribute('aria-expanded', 'true');
      var selected = dd.options.filter(function (o) { return o.dataset.value === dd.select.value; })[0];
      this.setActiveOption(dd, selected || dd.options[0]);
      dd.list.focus();
    }

    closeDropdown(dd, refocus) {
      if (!dd || dd.list.hidden) return;
      dd.list.hidden = true;
      dd.root.classList.remove('is-open');
      dd.trigger.setAttribute('aria-expanded', 'false');
      dd.list.removeAttribute('aria-activedescendant');
      if (refocus) dd.trigger.focus();
    }

    setActiveOption(dd, opt) {
      if (!opt) return;
      dd.options.forEach(function (o) { o.classList.toggle('is-active', o === opt); });
      dd.active = opt;
      dd.list.setAttribute('aria-activedescendant', opt.id);
      opt.scrollIntoView({ block: 'nearest' });
    }

    chooseOption(dd, opt) {
      if (opt.getAttribute('aria-disabled') === 'true') return;
      this.closeDropdown(dd, true);
      if (dd.select.value === opt.dataset.value) return;
      dd.select.value = opt.dataset.value;
      dd.select.dispatchEvent(new Event('change'));
    }

    // Price chips in the list ("+ £56") and the closed trigger
    // ("Box Frame | Oak - £56"), matching the Figma states.
    syncDropdown(dd, selectedValue, addons) {
      if (!dd) return;
      dd.options.forEach(function (opt) {
        var priceEl = qs(opt, '[data-cv2-price]');
        if (priceEl) priceEl.textContent = this.addonText(addons[opt.dataset.value]);
        opt.setAttribute('aria-selected', String(opt.dataset.value === selectedValue));
      }, this);

      var selected = dd.options.filter(function (o) { return o.dataset.value === selectedValue; })[0] || dd.options[0];
      if (!selected) return;
      var swatch = qs(selected, '.cv2-swatch');
      var label = qs(selected, '.cv2-dropdown__label');
      dd.current.innerHTML = '';
      if (swatch) dd.current.appendChild(swatch.cloneNode(true));
      var text = document.createElement('span');
      text.className = 'cv2-dropdown__label';
      text.textContent = label ? label.textContent : selectedValue;
      dd.current.appendChild(text);
      var price = document.createElement('span');
      price.className = 'cv2-dropdown__price';
      price.textContent = this.addonText(addons[selected.dataset.value]);
      dd.current.appendChild(price);
    }

    updatePrice() {
      if (!this.priceEl) return;
      var amount = this.price(this.state.size, this.state.frame, this.state.mount);
      this.priceEl.textContent = this.formatMoney(amount);
    }

    updateVariant() {
      if (this.priceSource !== 'variants') return; // placeholder mode: Add to cart stays disabled, nothing to resolve

      var variant = this.findVariant(this.state.size, this.state.frame, this.state.mount);
      if (variant) {
        if (this.hiddenInput) {
          this.hiddenInput.value = variant.id;
          this.hiddenInput.disabled = false;
        }
        if (this.submitButton) {
          this.submitButton.disabled = !variant.available;
          this.submitButton.toggleAttribute('aria-disabled', !variant.available);
        }
        this.syncUrl(variant.id);
      } else {
        if (this.hiddenInput) this.hiddenInput.disabled = true;
        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }
    }

    syncUrl(variantId) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  // Registered as <mockup-configurator> because that's the wrapper already
  // structural to sections/main-product.liquid's hooks (see "Where it plugs
  // in" in the main-product-page build doc) — same element, new behaviour.
  // Also registered as <configurator-v2> for the standalone section/template
  // from the original build plan's phase 3, left in the repo unreferenced
  // pending the step-8 cleanup once Ryan approves the integrated version.
  // Two distinct (if trivial) subclasses because the Custom Elements
  // registry refuses to register the same class under two tag names.
  class MockupConfiguratorElement extends ConfiguratorV2 {}
  class ConfiguratorV2Element extends ConfiguratorV2 {}

  if (!customElements.get('mockup-configurator')) {
    customElements.define('mockup-configurator', MockupConfiguratorElement);
  }
  if (!customElements.get('configurator-v2')) {
    customElements.define('configurator-v2', ConfiguratorV2Element);
  }
})();
